import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient, useAuthStore } from '@dukene/shared';
import {
  Receipt,
  CheckCircle,
  Calendar,
  Check,
  CreditCard,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Percent,
  Store,
  Share2,
  Wallet,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';

interface SubscriptionOverview {
  currentPlan: 'BASIC' | 'PRO' | 'MAX' | 'ENTERPRISE';
  monthlyCost: number;
  yearlyCost: number;
  transactionFeePercentage?: number;
  storeLimit?: number | null;
  salesChannelLimit?: number | null;
  nextBillingDate: string;
  walletBalance?: number;
  walletId?: string;
}

interface Transaction {
  id: string;
  type: string;
  amount: number;
  status: string;
  description: string;
  date: string;
  isCredit: boolean;
}

interface TransactionsResponse {
  items: Transaction[];
  meta: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    hasMore: boolean;
  };
}

const PLANS = [
  {
    id: 'BASIC',
    name: 'Basic',
    description: 'Essential tools to launch your store with zero subscription cost.',
    monthlyPrice: 0,
    transactionFee: '6%',
    storeLimit: '1 Store',
    salesChannelLimit: '1 Channel (Online Store only)',
    features: [
      'Free monthly subscription',
      '6% platform transaction fee on all orders',
      '1 Store included',
      'Online Store sales channel only',
      'Unlimited products & inventory',
      'Standard M-PESA & Card checkout',
      'Basic sales & order reporting',
    ],
  },
  {
    id: 'PRO',
    name: 'Pro',
    description: 'Scale up with 2 stores, 3 sales channels per store, and a lower 3% tx fee.',
    monthlyPrice: 3500,
    transactionFee: '3%',
    storeLimit: '2 Stores max',
    salesChannelLimit: 'Up to 3 channels on each store',
    features: [
      'KES 3,500 / month',
      '3% platform transaction fee',
      'Up to 2 stores included',
      'Up to 3 sales channels per store',
      'Custom domain & SSL certificate',
      'WhatsApp automated notifications',
      'Advanced customer & sales analytics',
    ],
    isPopular: true,
  },
  {
    id: 'MAX',
    name: 'Max',
    description: 'Multi-store expansion with up to 5 stores, all sales channels, and 2% tx fee.',
    monthlyPrice: 9500,
    transactionFee: '2%',
    storeLimit: 'Up to 5 Stores',
    salesChannelLimit: 'Access to all sales channels',
    features: [
      'KES 9,500 / month',
      '2% platform transaction fee',
      'Up to 5 stores included',
      'Access to all sales channels (POS, Meta, Google, etc.)',
      'Full API access & custom webhooks',
      'White-label customer checkout',
      'Priority merchant support',
    ],
  },
];

export default function BillingSettings() {
  const { user } = useAuthStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const isOwner =
    user?.role === 'OWNER' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'MERCHANT' ||
    user?.membershipRole === 'OWNER' ||
    user?.membershipRole === 'CO_OWNER';

  const { data: overview, isLoading: isLoadingOverview } = useQuery<SubscriptionOverview>({
    queryKey: ['billing-overview'],
    queryFn: () => apiClient.get('/billing/overview'),
    enabled: isOwner,
  });

  const { data: transactionsData, isLoading: isLoadingTransactions } = useQuery<TransactionsResponse>({
    queryKey: ['billing-transactions', currentPage],
    queryFn: () => apiClient.get(`/billing/transactions?limit=20&page=${currentPage}`),
    enabled: isOwner,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!isOwner) {
    return (
      <div className="py-16 text-center max-w-md mx-auto space-y-3">
        <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-950 flex items-center justify-center text-amber-700 dark:text-amber-300 mx-auto">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h2 className="text-base font-bold text-gray-900 dark:text-white">Owner access only</h2>
      </div>
    );
  }

  const currentPlanId = overview?.currentPlan || user?.plan || 'BASIC';
  const currentPlanMeta = PLANS.find((p) => p.id === currentPlanId) || PLANS[0];

  return (
    <div className="space-y-6 pb-20 max-w-5xl animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#D2D5D9] dark:border-slate-800">
        <div>
          <h1 className="text-xl font-bold text-[#202223] dark:text-white">Plan and billing</h1>
        </div>
      </div>

      {/* Current Plan Overview Card */}
      <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-[11px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300">
                <CheckCircle className="w-3.5 h-3.5" />
                ACTIVE PLAN
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] font-semibold bg-blue-50 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-900">
                <Percent className="w-3 h-3" />
                {overview?.transactionFeePercentage !== undefined
                  ? `${overview.transactionFeePercentage}% Tx Fee`
                  : `${currentPlanMeta.transactionFee} Tx Fee`}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] font-semibold bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300">
                <Store className="w-3 h-3" />
                {currentPlanMeta.storeLimit}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] font-semibold bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300">
                <Share2 className="w-3 h-3" />
                {currentPlanMeta.salesChannelLimit}
              </span>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-[#202223] dark:text-white">
                {isLoadingOverview ? 'Loading plan...' : `${overview?.currentPlan || 'BASIC'} Plan`}
              </h2>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-4 md:pt-0 border-t md:border-t-0 border-[#E1E4E8] dark:border-slate-800">
            <div className="px-4 py-3 bg-[#F1F2F4] dark:bg-slate-800/60 rounded-lg border border-[#D2D5D9] dark:border-slate-700 min-w-[180px]">
              <div className="flex items-center gap-1.5 text-[#5C5F62] dark:text-slate-400 text-[11px] font-semibold uppercase tracking-wider">
                <Calendar className="w-3.5 h-3.5" />
                <span>Subscription Rate</span>
              </div>
              <p className="text-base font-bold text-[#202223] dark:text-white mt-1">
                {currentPlanMeta.monthlyPrice === 0
                  ? 'Free'
                  : `${formatCurrency(overview?.monthlyCost || currentPlanMeta.monthlyPrice)} / mo`}
              </p>
              <p className="text-[11px] text-[#5C5F62] dark:text-slate-400 mt-0.5">
                {currentPlanMeta.monthlyPrice === 0
                  ? '6% per processed transaction'
                  : `${currentPlanMeta.transactionFee} platform fee`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Credits Wallet Banner Card */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-6 border border-slate-700">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/30">
            <Wallet className="w-6 h-6" />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-white">Credits Wallet for Transaction Fees</h3>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                AUTO-DEDUCT
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right hidden md:block">
            <p className="text-[11px] text-slate-400 uppercase font-semibold">Current Balance</p>
            <p className="text-lg font-black text-emerald-400">
              {overview?.walletBalance !== undefined ? formatCurrency(overview.walletBalance) : 'KES 200.00'}
            </p>
          </div>
          <Link
            to="/settings/wallet"
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-colors inline-flex items-center gap-1.5 shadow-sm"
          >
            <Wallet className="w-4 h-4" />
            Manage Wallet & Top Up
          </Link>
        </div>
      </div>

      {/* Available Plans Section */}
      <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#E1E4E8] dark:border-slate-800">
          <div>
            <h2 className="text-sm font-bold text-[#202223] dark:text-white">Available subscription tiers</h2>
          </div>

          {/* Segmented Cycle Switch */}
          <div className="inline-flex items-center p-1 rounded-lg bg-[#F1F2F4] dark:bg-slate-800 border border-[#D2D5D9] dark:border-slate-700 self-start sm:self-auto">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white dark:bg-slate-900 text-[#202223] dark:text-white shadow-sm'
                  : 'text-[#5C5F62] dark:text-slate-400 hover:text-[#202223]'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${
                billingCycle === 'yearly'
                  ? 'bg-white dark:bg-slate-900 text-[#202223] dark:text-white shadow-sm'
                  : 'text-[#5C5F62] dark:text-slate-400 hover:text-[#202223]'
              }`}
            >
              <span>Yearly</span>
              <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                Save 16%
              </span>
            </button>
          </div>
        </div>

        {/* Plan Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {PLANS.map((plan) => {
            const isFree = plan.monthlyPrice === 0;
            const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.monthlyPrice * 10;
            const isCurrent = overview?.currentPlan === plan.id;

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-xl p-5 transition-all ${
                  plan.isPopular
                    ? 'border-2 border-[#202223] dark:border-white bg-white dark:bg-slate-900 shadow-md'
                    : 'border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-slate-900'
                }`}
              >
                {plan.isPopular && (
                  <div className="absolute -top-3 left-5 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#202223] dark:bg-white text-white dark:text-slate-900 text-[10px] font-bold uppercase tracking-wider">
                    <Sparkles className="w-3 h-3" />
                    Recommended
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#202223] dark:text-white">{plan.name}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                      isFree
                        ? 'bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300'
                        : 'bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300'
                    }`}>
                      {plan.transactionFee} Tx Fee
                    </span>
                  </div>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 min-h-[32px]">
                    {plan.description}
                  </p>
                </div>

                <div className="mb-4 flex items-baseline gap-1">
                  {isFree ? (
                    <span className="text-3xl font-extrabold text-[#202223] dark:text-white">
                      Free
                    </span>
                  ) : (
                    <>
                      <span className="text-2xl font-bold text-[#202223] dark:text-white">
                        {formatCurrency(price)}
                      </span>
                      <span className="text-xs text-[#5C5F62] dark:text-slate-400 font-medium">
                        /{billingCycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    </>
                  )}
                </div>

                {/* Plan Highlights */}
                <div className="grid grid-cols-2 gap-2 mb-4 p-2.5 bg-[#F1F2F4] dark:bg-slate-800/60 rounded-lg text-[11px] font-medium text-gray-700 dark:text-slate-300">
                  <div className="flex items-center gap-1.5">
                    <Store className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="truncate">{plan.storeLimit}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Percent className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span>{plan.transactionFee} Fee</span>
                  </div>
                </div>

                <ul className="flex-1 space-y-2.5 mb-6 pt-2 border-t border-[#E1E4E8] dark:border-slate-800">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-[#202223] dark:text-slate-200">
                      <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  disabled={isCurrent}
                  className={`w-full h-9 rounded-lg font-semibold text-xs transition-all ${
                    isCurrent
                      ? 'bg-[#F1F2F4] dark:bg-slate-800 text-[#5C5F62] dark:text-slate-400 cursor-default'
                      : plan.isPopular
                      ? 'bg-[#202223] dark:bg-white text-white dark:text-slate-900 hover:opacity-90 shadow-sm'
                      : 'bg-[#F1F2F4] dark:bg-slate-800 text-[#202223] dark:text-white hover:bg-[#E1E4E8] dark:hover:bg-slate-700'
                  }`}
                >
                  {isCurrent ? 'Current active plan' : 'Select plan tier'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Billing History Section */}
      <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
        <div className="px-5 py-3.5 border-b border-[#E1E4E8] dark:border-slate-800 flex items-center justify-between bg-[#F1F2F4] dark:bg-slate-800/50">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-[#5C5F62]" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
              Billing & invoice history
            </h2>
          </div>
        </div>

        {isLoadingTransactions ? (
          <div className="p-16 flex flex-col items-center justify-center space-y-3">
            <Loader2 className="w-6 h-6 text-[#5C5F62] animate-spin" />
            <p className="text-xs text-[#5C5F62]">Loading transaction history...</p>
          </div>
        ) : !transactionsData?.items || !Array.isArray(transactionsData.items) || transactionsData.items.length === 0 ? (
          <div className="p-12 text-center text-xs text-[#5C5F62] dark:text-slate-400">
            <CreditCard className="mx-auto h-8 w-8 text-[#D2D5D9] mb-2" />
            <p className="font-semibold text-[#202223] dark:text-white">No invoices yet</p>
            <p className="text-[#5C5F62] mt-0.5">Subscription cycles and payments will appear here once processed.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E1E4E8] dark:divide-slate-800">
              <thead className="bg-[#F8F9FA] dark:bg-slate-800/80">
                <tr>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-5 py-3 text-right text-[11px] font-bold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800">
                {(transactionsData?.items || []).map((tx) => (
                  <tr key={tx.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="px-5 py-3.5 whitespace-nowrap text-xs text-[#5C5F62] dark:text-slate-400">
                      {format(new Date(tx.date), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-xs font-bold text-[#202223] dark:text-white">
                      {tx.description}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                          tx.status === 'COMPLETED'
                            ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300'
                            : tx.status === 'FAILED'
                            ? 'bg-red-100 dark:bg-red-950/60 text-red-800 dark:text-red-300'
                            : 'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300'
                        }`}
                      >
                        {tx.status}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-right text-xs font-bold text-[#202223] dark:text-white">
                      {formatCurrency(tx.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {transactionsData?.meta && transactionsData.meta.pages > 1 && (
          <div className="px-5 py-3 border-t border-[#E1E4E8] dark:border-slate-800 flex items-center justify-between bg-[#F8F9FA] dark:bg-slate-800/40">
            <span className="text-xs text-[#5C5F62] dark:text-slate-400">
              Page {currentPage} of {transactionsData.meta.pages}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-medium text-[#202223] dark:text-white disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Prev
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(transactionsData.meta.pages, p + 1))}
                disabled={currentPage >= transactionsData.meta.pages}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-medium text-[#202223] dark:text-white disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
